import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    ShoppingCart,
    Trash2,
    Minus,
    Plus,
    ArrowLeft,
    Package,
    CreditCard
} from 'lucide-react';
import PageLayout from '../../components/Layout/PageLayout';
import PageHeader from '../../components/PageHeader/PageHeader';
import RFQButton from '../../components/rfq/RFQButton';
import BrandPlaceholder from '../../components/product/BrandPlaceholder';
import { useCart } from '../../context/CartContext';
import { getPriceAndAvailability } from '../../api/ingram';
import './CartPage.scss';

// Per-line image lookup. The `item.image` snapshotted at add-to-cart time may
// be null (post BrandPlaceholder change) or a stale URL — hit our image
// endpoint to get the real photo. If nothing resolves, render BrandPlaceholder.
function CartItemImage({ item }) {
    const [imageUrl, setImageUrl] = useState(item.imageUrl || null);
    const [imgError, setImgError] = useState(false);
    useEffect(() => {
        if (!item.partNumber) return;
        if (item.imageUrl) { setImageUrl(item.imageUrl); return; }
        const apiBase = process.env.REACT_APP_API_URL || '/api';
        const params = new URLSearchParams();
        if (item.manufacturer) params.set('brand', item.manufacturer);
        if (item.upc) params.set('upc', item.upc);
        if (item.name) params.set('name', item.name);
        const ingramPn = item.ingramPartNumber || item.id;
        if (ingramPn) params.set('ingramPartNumber', ingramPn);
        let cancelled = false;
        fetch(`${apiBase}/products/image/${encodeURIComponent(item.partNumber)}?${params.toString()}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((res) => { if (!cancelled && res?.imageUrl) setImageUrl(res.imageUrl); })
            .catch(() => { /* placeholder stays */ });
        return () => { cancelled = true; };
    }, [item.partNumber, item.manufacturer, item.upc, item.name, item.imageUrl]);
    if (!imageUrl || imgError) {
        return <BrandPlaceholder brand={item.manufacturer} name={item.name} size="thumb" />;
    }
    return (
        <img
            src={imageUrl}
            alt={item.name}
            onError={() => setImgError(true)}
        />
    );
}

const CartPage = () => {
    const {
        items,
        itemCount,
        formattedSubtotal,
        removeItem,
        updateQuantity,
        clearCart,
        hydratePrices
    } = useCart();

    const [pricing, setPricing] = useState(false);

    // Re-price cart items live (same source as checkout) so line prices + subtotal
    // reflect real Ingram pricing (12% markup), not the stale "Contact for Price"
    // captured when an item was added from the search grid.
    const skuKey = items.map(i => i.id).sort().join(',');
    useEffect(() => {
        let cancelled = false;
        const skus = items.map(i => i.id).filter(Boolean);
        if (skus.length === 0) return;

        (async () => {
            setPricing(true);
            try {
                // /api/products/price caps at 50 SKUs — chunk and merge.
                const merged = {};
                for (let i = 0; i < skus.length; i += 50) {
                    const batch = skus.slice(i, i + 50);
                    const map = await getPriceAndAvailability(batch);
                    Object.assign(merged, map || {});
                }
                if (!cancelled) hydratePrices(merged);
            } catch (err) {
                console.error('Failed to refresh cart prices:', err);
            } finally {
                if (!cancelled) setPricing(false);
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skuKey]);

    // Handle quantity changes
    const handleIncreaseQuantity = (item) => {
        updateQuantity(item.id, item.quantity + 1);
    };

    const handleDecreaseQuantity = (item) => {
        if (item.quantity > 1) {
            updateQuantity(item.id, item.quantity - 1);
        }
    };

    // Empty cart state
    if (items.length === 0) {
        return (
            <PageLayout>
                <PageHeader
                    title="Shopping Cart"
                    subtitle="Your cart is empty"
                    breadcrumbs={[
                        { label: 'Home', path: '/' },
                        { label: 'Cart' }
                    ]}
                />
                <section className="jk-cart">
                    <div className="jk-cart__container">
                        <div className="jk-cart__empty">
                            <ShoppingCart size={64} />
                            <h2>Your cart is empty</h2>
                            <p>Looks like you haven't added any products to your cart yet.</p>
                            <Link to="/accessories" className="jk-btn jk-btn--primary">
                                <Package size={18} />
                                Browse Products
                            </Link>
                        </div>
                    </div>
                </section>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <PageHeader
                title="Shopping Cart"
                subtitle={`${itemCount} ${itemCount === 1 ? 'item' : 'items'} in your cart`}
                breadcrumbs={[
                    { label: 'Home', path: '/' },
                    { label: 'Cart' }
                ]}
            />

            <section className="jk-cart">
                <div className="jk-cart__container">
                    <div className="jk-cart__layout">
                        {/* Cart Items */}
                        <div className="jk-cart__items">
                            <div className="jk-cart__header">
                                <h2>Cart Items</h2>
                                <button
                                    className="jk-cart__clear-btn"
                                    onClick={clearCart}
                                >
                                    <Trash2 size={16} />
                                    Clear Cart
                                </button>
                            </div>

                            <div className="jk-cart__list">
                                {items.map((item) => (
                                    <div key={item.id} className="jk-cart__item">
                                        <Link
                                            to={`/product/${item.id}`}
                                            className="jk-cart__item-image"
                                        >
                                            <CartItemImage item={item} />
                                        </Link>

                                        <div className="jk-cart__item-details">
                                            <Link
                                                to={`/product/${item.id}`}
                                                className="jk-cart__item-name"
                                            >
                                                {item.name}
                                            </Link>
                                            <span className="jk-cart__item-manufacturer">
                                                {item.manufacturer}
                                            </span>
                                            <span className="jk-cart__item-part">
                                                Part #: {item.partNumber}
                                            </span>
                                        </div>

                                        <div className="jk-cart__item-quantity">
                                            <button
                                                onClick={() => handleDecreaseQuantity(item)}
                                                disabled={item.quantity <= 1}
                                                aria-label="Decrease quantity"
                                            >
                                                <Minus size={16} />
                                            </button>
                                            <span>{item.quantity}</span>
                                            <button
                                                onClick={() => handleIncreaseQuantity(item)}
                                                aria-label="Increase quantity"
                                            >
                                                <Plus size={16} />
                                            </button>
                                        </div>

                                        <div className="jk-cart__item-price">
                                            {item.price}
                                        </div>

                                        <button
                                            className="jk-cart__item-remove"
                                            onClick={() => removeItem(item.id)}
                                            aria-label="Remove item"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <Link to="/accessories" className="jk-cart__continue">
                                <ArrowLeft size={18} />
                                Continue Shopping
                            </Link>
                        </div>

                        {/* Order Summary */}
                        <aside className="jk-cart__summary">
                            <h3>Order Summary</h3>

                            <div className="jk-cart__summary-row">
                                <span>Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'})</span>
                                <span>{formattedSubtotal}</span>
                            </div>

                            <div className="jk-cart__summary-row">
                                <span>Shipping</span>
                                <span>Calculated at checkout</span>
                            </div>

                            <div className="jk-cart__summary-row">
                                <span>Tax</span>
                                <span>Calculated at checkout</span>
                            </div>

                            <div className="jk-cart__summary-total">
                                <span>Estimated Total</span>
                                <span>{formattedSubtotal}</span>
                            </div>

                            {pricing && (
                                <div className="jk-cart__summary-note">
                                    <p>Updating prices…</p>
                                </div>
                            )}

                            <div className="jk-cart__summary-actions">
                                <Link
                                    to="/checkout"
                                    className="jk-btn jk-btn--primary jk-btn--full"
                                    style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                                >
                                    <CreditCard size={18} /> Proceed to Checkout
                                </Link>
                                <RFQButton
                                    cartItems={items}
                                    variant="primary"
                                />
                            </div>

                            <div className="jk-cart__summary-features">
                                <div className="jk-cart__summary-feature">
                                    <Package size={18} />
                                    <span>Authorized IT Reseller</span>
                                </div>
                                <div className="jk-cart__summary-feature">
                                    <ShoppingCart size={18} />
                                    <span>Government & Enterprise Pricing</span>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>
        </PageLayout>
    );
};

export default CartPage;
